// The in-memory API's managed mode (MANAGED-POLICY; services/api/src/routes/agent-requests.ts)
// with the real routes' rules: the mode lives on the company (`agent_mode`); an agency admin
// (`me.agency.role === "admin"` with an operator grant) switches it; in a managed company the
// add-agent routes answer 403 `agent_mode_managed` to everyone but the agency's operators
// (`managedGate`); a person files a request (a DE: managers only), managers and operators list
// all, others their own; an operator resolves an open one once (409 after); the operator's
// queue lists the requests of every company they hold an operator grant on.
import {
  AgentRequestCreate,
  AgentRequestResolve,
  HelperRequestStageMove,
} from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import type { Ctx, Handler, Principal } from "./fake";
import { json, notFound, problem, validation } from "./fake";
import type { FakeCompany } from "./fake-data";
import type { ComposedHandler } from "./fake-merge";

type View = OperationOutput<"agentRequests.create">;
type ModeView = OperationOutput<"agentMode.get">;

/** The routes that add a DE or an AG: refused in a managed company to all but the agency. */
export const MANAGED_ADD_ROUTES: readonly RouteId[] = [
  "digitalEmployees.create",
  "agents.deploySelfServe",
  "workforceAgents.create",
  "crews.hireAsDe",
];

const managedRefusal = () =>
  problem(
    403,
    "agent_mode_managed",
    "This company's agency adds agents: send a request instead",
    "Your agency adds agents for this company: send a request (Request an agent) instead",
  );

/** Wraps an add-agent handler with the managed-mode rule (domain/agent-requests/mode.ts). */
export function managedGate(h: ComposedHandler): ComposedHandler {
  return (ctx: Ctx) => {
    const p = ctx.principal;
    if (p && p.company.agent_mode === "managed" && !p.operator) return managedRefusal();
    return h(ctx);
  };
}

const SLUG = /^[a-z0-9][a-z0-9-]{0,63}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function createAgentRequestHandlers(deps: {
  now: () => Date;
  uid: () => string;
  companies: () => readonly FakeCompany[];
  /** A DE membership of the company (a fulfilled DE request may name one). */
  isDe: (companyId: string, id: string) => boolean;
  /** An AG of the company (a fulfilled AG request may name one). */
  isAgent: (p: Principal, id: string) => boolean;
}): { handlers: Partial<Record<RouteId, Handler>>; requestsOf: (companyId: string) => View[] } {
  const store = new Map<string, View[]>();
  const requestsOf = (companyId: string) => {
    let list = store.get(companyId);
    if (!list) {
      list = [];
      store.set(companyId, list);
    }
    return list;
  };

  const isAgencyAdmin = (p: Principal) => p.operator && p.me.agency?.role === "admin";
  const self = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id && m.kind === "person") ?? null;

  function modeView(p: Principal): ModeView {
    return {
      mode: p.company.agent_mode,
      can_change: isAgencyAdmin(p),
      can_add: p.company.agent_mode === "self_serve" || p.operator,
    };
  }

  const newestFirst = (a: View, b: View) =>
    b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id);

  const handlers: Partial<Record<RouteId, Handler>> = {
    "agentMode.get": ({ principal }) => {
      if (!principal) return notFound();
      return json(modeView(principal));
    },
    "agentMode.put": ({ principal, body }) => {
      if (!principal) return notFound();
      if (!principal.operator) return problem(403, "forbidden", "Forbidden");
      if (!isAgencyAdmin(principal))
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "an admin of the agency switches who adds agents",
        );
      const mode = (body as { mode?: unknown } | null)?.mode;
      if (mode !== "managed" && mode !== "self_serve")
        return validation("mode: managed or self_serve");
      if (principal.company.agent_mode !== mode) {
        principal.company.agent_mode = mode;
        principal.company.updated_at = deps.now().toISOString();
      }
      return json(modeView(principal));
    },
    "agentRequests.list": ({ principal, url }) => {
      if (!principal) return notFound();
      const me = self(principal);
      if (!principal.manager && !me) return problem(403, "forbidden", "Forbidden");
      const state = url.searchParams.get("state");
      const kind = url.searchParams.get("kind");
      const de = url.searchParams.get("de_membership_id");
      const data = requestsOf(principal.company.id)
        .filter((r) => !state || r.state === state)
        .filter((r) => !kind || r.kind === kind)
        .filter((r) => !de || r.de_membership_id === de)
        .filter((r) => principal.manager || r.requested_by_membership_id === me?.membership_id)
        .sort(newestFirst)
        .slice(0, 200);
      return json({ data });
    },
    "agentRequests.create": ({ principal, body }) => {
      if (!principal) return notFound();
      const me = self(principal);
      if (!me || principal.roles.every((r) => r === "client_viewer"))
        return problem(403, "forbidden", "Forbidden", "a person asks for an agent");
      // The API's own body schema first (Codex review: the fake accepts nothing the API refuses).
      const parsed = AgentRequestCreate.safeParse(body ?? {});
      if (!parsed.success) return validation(parsed.error.issues[0]?.message ?? "invalid body");
      const b = parsed.data as Record<string, unknown>;
      const role = typeof b.role === "string" ? b.role.trim() : "";
      if (b.kind !== "de" && b.kind !== "ag" && b.kind !== "helper")
        return validation("kind: de, ag or helper");
      // HELPERS: a helper for one of the company's DEs.
      const de = b.kind === "helper" ? String(b.de_membership_id).toLowerCase() : null;
      if (de && !deps.isDe(principal.company.id, de))
        return validation("de_membership_id: not a digital employee of this company");
      if (!role || role.length > 200) return validation("role: 1 to 200 characters");
      const templateKey = b.template_key ?? null;
      if (templateKey !== null && (typeof templateKey !== "string" || !SLUG.test(templateKey)))
        return validation("template_key: a catalog role key");
      const note = typeof b.note === "string" ? b.note.trim() : null;
      if (note && note.length > 2000) return validation("note: at most 2000 characters");
      if (b.kind === "de" && !principal.manager)
        return problem(
          403,
          "forbidden",
          "Forbidden",
          "an owner or admin asks for a digital employee",
        );
      const at = deps.now().toISOString();
      const row: View = {
        company_id: principal.company.id,
        id: deps.uid(),
        kind: b.kind,
        role,
        template_key: templateKey as string | null,
        note: note || null,
        requested_by_membership_id: me.membership_id,
        requested_by_name: principal.me.user.name,
        state: "open",
        resolution_note: null,
        resolved_by_user_id: null,
        resolved_at: null,
        fulfilled_ref_id: null,
        de_membership_id: de,
        stage: de ? "requested" : null,
        stage_changed_at: de ? at : null,
        quote: null,
        actor_kind: "person",
        actor_membership_id: me.membership_id,
        created_at: at,
        updated_at: at,
      };
      requestsOf(principal.company.id).push(row);
      return json(row, 201);
    },
    // HELPERS: the client approves a quote; the operator moves a helper request on.
    "agentRequests.approve": ({ principal, params }) => {
      if (!principal) return notFound();
      const row = requestsOf(principal.company.id).find((r) => r.id === params.id);
      if (row?.kind !== "helper") return notFound();
      if (!principal.manager && row.requested_by_membership_id !== self(principal)?.membership_id)
        return problem(403, "forbidden", "Forbidden");
      if (row.stage !== "quoted")
        return problem(409, "conflict", "Conflict", `this request is ${row.stage}, not quoted`);
      const at = deps.now().toISOString();
      Object.assign(row, { stage: "approved", stage_changed_at: at, updated_at: at });
      return json(row);
    },
    "agentRequests.stage": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.operator) return problem(403, "forbidden", "Forbidden");
      const row = requestsOf(principal.company.id).find((r) => r.id === params.id);
      if (row?.kind !== "helper" || !row.stage) return notFound();
      const parsed = HelperRequestStageMove.safeParse(body ?? {});
      if (!parsed.success) return validation(parsed.error.issues[0]?.message ?? "invalid body");
      const b = parsed.data;
      const next: Record<string, readonly string[]> = {
        requested: ["quoted", "declined"],
        quoted: ["quoted", "approved", "declined"],
        approved: ["paid", "declined"],
        paid: ["building", "declined"],
        building: ["added", "declined"],
      };
      if (!(next[row.stage] ?? []).includes(b.stage))
        return problem(409, "conflict", "Conflict", `a ${row.stage} request cannot be ${b.stage}`);
      if (b.stage === "quoted" && !b.quote) return validation("quote: required with quoted");
      const at = deps.now().toISOString();
      const closes = b.stage === "added" || b.stage === "declined";
      Object.assign(row, {
        stage: b.stage,
        stage_changed_at: at,
        ...(b.quote ? { quote: b.quote } : {}),
        ...(closes
          ? {
              state: b.stage === "added" ? "fulfilled" : "declined",
              resolution_note: b.note ?? null,
              resolved_by_user_id: principal.me.user.id,
              resolved_at: at,
            }
          : {}),
        updated_at: at,
      });
      return json(row);
    },
    "agentRequests.resolve": ({ principal, params, body }) => {
      if (!principal) return notFound();
      if (!principal.operator) return problem(403, "forbidden", "Forbidden");
      const row = requestsOf(principal.company.id).find((r) => r.id === params.id);
      if (!row) return notFound();
      if (row.state !== "open")
        return problem(409, "conflict", "Conflict", `this request is already ${row.state}`);
      const parsed = AgentRequestResolve.safeParse(body ?? {});
      if (!parsed.success) return validation(parsed.error.issues[0]?.message ?? "invalid body");
      const b = parsed.data as Record<string, unknown>;
      if (b.outcome !== "fulfilled" && b.outcome !== "declined")
        return validation("outcome: fulfilled or declined");
      const ref = typeof b.fulfilled_ref_id === "string" ? b.fulfilled_ref_id.toLowerCase() : null;
      if (ref && (!UUID.test(ref) || b.outcome !== "fulfilled"))
        return validation("fulfilled_ref_id: only a fulfilled request names what it became");
      const known =
        ref === null ||
        (row.kind === "de" ? deps.isDe(principal.company.id, ref) : deps.isAgent(principal, ref));
      if (!known)
        return validation(
          `fulfilled_ref_id: not ${row.kind === "de" ? "a digital employee" : "an agent"} of this company`,
        );
      const note = typeof b.note === "string" ? b.note.trim() : null;
      const at = deps.now().toISOString();
      Object.assign(row, {
        state: b.outcome,
        resolution_note: note || null,
        resolved_by_user_id: principal.me.user.id,
        resolved_at: at,
        fulfilled_ref_id: ref,
        actor_kind: "agency_user",
        actor_membership_id: null,
        updated_at: at,
      });
      return json(row);
    },
    "operator.agentRequests.list": ({ me, url }) => {
      if (!me.agency) return notFound();
      const state = url.searchParams.get("state") ?? "open";
      const ids = new Set(
        me.agency.grants.filter((g) => g.level === "operator").map((g) => g.company_id),
      );
      const data = deps
        .companies()
        .filter((c) => ids.has(c.id))
        .flatMap((c) =>
          requestsOf(c.id)
            .filter((r) => r.state === state)
            .map((request) => ({ company: { id: c.id, slug: c.slug, name: c.name }, request })),
        )
        .sort((a, b) => newestFirst(a.request, b.request));
      return json({ data, complete: true, incomplete_reasons: [] });
    },
  };
  return { handlers, requestsOf };
}
